"use client";

import { useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react/dist/ssr";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Button } from "@/src/components/ui/Button";
import { Heading, Highlight } from "@/src/components/ui/Typography";
import { StatRow, type StatItemData } from "@/src/components/molecules/StatRow";
import {
  TestimonialCard,
  TestimonialMobileRow,
  type TestimonialItemData,
} from "@/src/components/molecules/TestimonialCard";
import { cn } from "@/src/lib/utils";

const COLLAPSED_REM = 8;
const collapsedWidth = `${COLLAPSED_REM}rem`;
const WINDOW_SIZE = 3;

interface TestimonialsInteractiveProps {
  items: TestimonialItemData[];
  stats: StatItemData[];
}

export function TestimonialsInteractive({ items, stats }: TestimonialsInteractiveProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileOpenIndex, setMobileOpenIndex] = useState(0);
  const [windowStart, setWindowStart] = useState(0);

  const visibleCount = Math.min(items.length, WINDOW_SIZE);
  const expandedWidth = `calc(100cqw - ${(visibleCount - 1) * COLLAPSED_REM}rem)`;
  const maxWindowStart = Math.max(items.length - WINDOW_SIZE, 0);
  const canSlideLeft = windowStart > 0;
  const canSlideRight = windowStart < maxWindowStart;

  const slideLeft = () => {
    if (!canSlideLeft) return;
    const poppedIndex = windowStart + WINDOW_SIZE - 1; // rightmost card leaving the frame
    const newWindowStart = windowStart - 1;
    setWindowStart(newWindowStart);
    if (activeIndex === poppedIndex) {
      // Its neighbour still in frame — now the rightmost card — opens instead.
      setActiveIndex(newWindowStart + WINDOW_SIZE - 1);
    }
  };

  const slideRight = () => {
    if (!canSlideRight) return;
    const poppedIndex = windowStart; // leftmost card leaving the frame
    const newWindowStart = windowStart + 1;
    setWindowStart(newWindowStart);
    if (activeIndex === poppedIndex) {
      // Its neighbour still in frame — now the leftmost card — opens instead.
      setActiveIndex(newWindowStart);
    }
  };

  const visibleItems = items.slice(windowStart, windowStart + WINDOW_SIZE);

  const navButtonStyle =
    "flex size-11 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-all duration-300 ease-[cubic-bezier(.22,.61,.36,1)] disabled:cursor-not-allowed disabled:opacity-30 enabled:hover:scale-105 enabled:hover:border-brand enabled:hover:bg-brand enabled:hover:text-white enabled:hover:shadow-[0_6px_20px_-4px_rgba(66,94,249,0.45)] enabled:active:scale-95";

  return (
    <Section className="bg-white">
      <Container>
        <div className="flex flex-wrap items-start justify-between gap-8">
          <Heading as="h2" size="displayLG" className="max-w-125">
            What Founders Say About <Highlight>TechEniac</Highlight>
          </Heading>

          {stats.length > 0 && <StatRow items={stats} />}
        </div>

        {/* Desktop: horizontal drawer — one testimonial open, the rest
            collapsed into narrow columns. `@container` exposes the row's own
            width to each card as `cqw` units, so the open card's content
            layer keeps a constant width no matter which card is animating.
            Only a window of up to WINDOW_SIZE cards renders at once; the nav
            buttons below slide that window across the full item list. */}
        <div className="@container mt-12 hidden min-h-96 items-stretch overflow-hidden rounded-(--radius-lg) border border-neutral-100 md:flex">
          {visibleItems.map((item, localIndex) => {
            const originalIndex = windowStart + localIndex;
            return (
              <TestimonialCard
                key={item._id}
                item={item}
                index={originalIndex + 1}
                open={activeIndex === originalIndex}
                onSelect={() => setActiveIndex(originalIndex)}
                collapsedWidth={collapsedWidth}
                expandedWidth={expandedWidth}
              />
            );
          })}
        </div>

        {items.length > WINDOW_SIZE && (
          <div className="mt-6 hidden justify-center gap-3 md:flex">
            <Button
              type="button"
              unstyled
              aria-label="Previous testimonials"
              onClick={slideLeft}
              disabled={!canSlideLeft}
              className={cn(navButtonStyle)}
            >
              <CaretLeft size={18} weight="bold" />
            </Button>
            <Button
              type="button"
              unstyled
              aria-label="Next testimonials"
              onClick={slideRight}
              disabled={!canSlideRight}
              className={cn(navButtonStyle)}
            >
              <CaretRight size={18} weight="bold" />
            </Button>
          </div>
        )}

        {/* Mobile: full vertical stack */}
        <div className="mt-10 rounded-(--radius-lg) border border-neutral-200 px-4 pt-4 md:hidden">
          {items.map((item, index) => (
            <TestimonialMobileRow
              key={item._id}
              item={item}
              index={index + 1}
              open={mobileOpenIndex === index}
              onToggle={() => setMobileOpenIndex(index)}
            />
          ))}
        </div>
      </Container>
    </Section>
  );
}
